export const utcDate = (date?: Date) => {
	if (date) {
		return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
	}
	const _date = new Date();
	return new Date(Date.UTC(_date.getFullYear(), _date.getMonth(), _date.getDate(), _date.getHours(), _date.getMinutes(), _date.getSeconds()));
};

interface IGetIntlDate {
	language?: string;
}

export const getInternationalDateFormat = (timestamp: number, options?: IGetIntlDate): string => {
	const date = new Date(timestamp);
	const _utcDate = utcDate(date);
	const intlDate = new Intl.DateTimeFormat(options?.language || window.navigator.language || 'en-US').format(new Date(_utcDate));
	return intlDate;
};

interface IGet24HrTime {
	showSeconds?: boolean;
	language?: string;
	hour12?: boolean;
	timeZone?: string;
}

export const get24HrTimeFromTimestamp = (timestamp: number, options?: IGet24HrTime): string => {
	const fullTime = new Date(timestamp)
		.toLocaleTimeString(
			window.navigator.language || 'en-US',
			{
				hour12: !!options?.hour12,
				...(options?.timeZone ? { timeZone: options.timeZone } : {})
			}
		);
	if (options?.showSeconds) return fullTime;
	const [hours, minutes] = fullTime.split(':');
	let _hours = hours;
	if (_hours === '24') {
		_hours = '00';
	}
	return _hours + ':' + minutes;
};

interface ISet24Hour {
	seconds?: string;
}

export const set24HrTimeOnTimestamp = (timestamp: number, time: string, options?: ISet24Hour): number => {
	const _date = new Date(timestamp);

	const [hours, minutes, seconds] = time.split(':');
	if (hours) {
		_date.setHours(Number(hours));
	}
	if (minutes) {
		_date.setMinutes(Number(minutes));
	}
	if (seconds) {
		_date.setSeconds(Number(seconds));
	}
	if (options?.seconds) _date.setSeconds(Number(options.seconds));
	return _date.getTime();
};

export const setDateOnTimestamp = (timestamp: number, date: string): number => {
	const _dateFromTimestamp = new Date(timestamp);
	const _dateToAdd = new Date(date);
	const year = _dateToAdd.getFullYear();
	const month = _dateToAdd.getMonth();
	const day = _dateToAdd.getDate();
	_dateFromTimestamp.setFullYear(year);
	_dateFromTimestamp.setMonth(month);
	_dateFromTimestamp.setDate(day);
	return _dateFromTimestamp.getTime();
};

export function roundMinutesToNearestFive(num: number): string {
	if (num <= 0) return '00';
	if (num >= 60) return '55';
	let formattedMinutes: number | string = num;
	if (num % 5 !== 0) {
		let updatedVal = '';
		if (num % 5 >= 3) {
			let roundedUpMin: number | string = (num + (5 - num % 5)); // round up to nearest 5
			if (roundedUpMin === 60) { // we can't have 60, so we set it back down to 55
				roundedUpMin = '55';
			}
			formattedMinutes = roundedUpMin;
		} else {
			formattedMinutes = (num - (num % 5)); // round down to nearest 5
			// we must have double digits, so if value is a single digit, add an extra stringified "0"
			if (formattedMinutes === 0) {
				formattedMinutes = '00';
			}
		}
		if (formattedMinutes.toString().length === 1) {
			updatedVal += `0${formattedMinutes}`;
		} else {
			updatedVal += formattedMinutes; // need to force it to be a string so we don't lose zero's
		}
		return updatedVal;
	}
	if (formattedMinutes.toString().length === 1) {
		formattedMinutes = '0' + formattedMinutes;
	}
	return formattedMinutes.toString();
}

export const isWithinNMinutesBeforeNow = (timestamp: number, minutesAgo = 10) => {
	const timeNMinutesAgo = Date.now() - (1000 * 60 * minutesAgo);
	return timestamp >= timeNMinutesAgo && timestamp < Date.now();
};

export const isWithinNMinutesAfterNow = (timestamp: number, minutesAhead = 10) => {
	const timeNMinutesInFuture = Date.now() + (1000 * 60 * minutesAhead);
	return timestamp <= timeNMinutesInFuture && timestamp > Date.now();
};

export const isInPast = (timestamp: number) => {
	return timestamp < Date.now();
};

export const isInFuture = (timestamp: number) => {
	return timestamp > Date.now();
};

export const BYPASS_EMAIL_INTERVALS = localStorage.getItem('BYPASS_EMAIL_INTERVALS') && process.env.REACT_APP_STAGE !== 'prod';

export const get2DigitTime = (timestamp: number, language?: string, options?: Intl.DateTimeFormatOptions | undefined) => {
	return new Date(timestamp).toLocaleString(language || window.navigator.language || 'en-US', { hour: '2-digit', minute: '2-digit', ...options });
};

type TFormat = "numeric" | "2-digit" | "long" | "short" | "narrow" | undefined
type IFormatMonthAndDay = (params: { date: Date, locale?: string, format?: TFormat }) => string;

export const formatMonthAndDay: IFormatMonthAndDay = ({
	date,
	locale = 'en',
	format = 'short' }) => {
	const dateObj = new Date(date);

	return dateObj.toLocaleDateString(locale, { month: format, day: '2-digit' });
};

const cache = new Map<string | number, Date>();

export const getDateWithCache = (date: string | number): Date => {
	const cachedDate = cache.get(date);
	if (cachedDate) {
		return cachedDate;
	}
	if (cache.size > 100) {
		cache.clear();
	}
	const newDate = new Date(date);
	cache.set(date, newDate);
	return newDate;
};

export const formatMsToHHMMSS = (ms: number) => {
	const seconds = Math.floor(ms / 1000);
	const hours = Math.floor(seconds / 3600);
	const hh = String(hours).padStart(2, '0');
	const mm = String(Math.floor((seconds / 60) % 60)).padStart(2, '0');
	const ss = String(seconds % 60).padStart(2, '0');
	if (hours) {
		return `${hh}:${mm}:${ss}`;
	}

	return `${mm}:${ss}`;
};

export const durationBetweenTimestamps = (start: string | number, end: string | number, short = false): string => {
	const startDate = getDateWithCache(start);
	const endDate = getDateWithCache(end);
	const startTimestamp = startDate.valueOf();
	const endTimestamp = endDate.valueOf();
	const ms = endTimestamp - startTimestamp;

	if (!short) {
		return formatMsToHHMMSS(ms);
	}

	const mins = Math.floor((ms / 1000) / 60);
	const hours = Math.floor(mins / 60);
	const days = Math.floor(hours / 24);

	if (mins <= 120) {
		return `${mins} min`;
	}

	if (hours <= 24) {
		return `${hours} hrs`;
	}

	return `${days} days`;
};